import { Component, ElementRef, OnInit } from '@angular/core';

import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { AppStateService } from '@services/app-state.service';
import { Logger } from '@utils/logger';
import { Reference } from '@bloomreach/spa-sdk';
import { Router } from '@angular/router';
import { WidenAsset } from '@types';
import { WidenService } from '@services/widen.service';
import { LinkService } from '@services/link.service';
import { ViewModeService } from '@services/view-mode.service';

/**
 * Logger
 */
const logger = Logger.getLogger('LitBannerComponent');

/**
 * Interface for the Banner Content
 */
interface LitBannerContent {
  category: string;
  title: string;
  subTitle: string;
  cards: CardContent[];
}

interface CardContent {
  iconPath: WidenAsset;
  icon: string;
  img: string;
  text: string;
  href: string;
  link: any; // create type
  externalLink?: boolean;
  isNewWindow?: boolean;
  signInRequired?: boolean;
}

/**
 * FT Lit Banner component
 *
 * This component is used at the top of a template and has it's own associated document type.
 *
 * Selector: `ft-banner`
 *
 * Exported as: `LitBannerComponent`
 */
@Component({
  selector: 'ft-lit-banner',
  templateUrl: './lit-banner.component.html',
})
export class LitBannerComponent
  extends AbstractBaseComponent
  implements OnInit {
  isEditMode = false;
  /**
   * Constructor
   */
  constructor(
    private router: Router,
    element: ElementRef,
    private appStateService: AppStateService,
    private widenService: WidenService,
    private ctaLinkService: LinkService,
    private viewModeService: ViewModeService
  ) {
    super();
    this.isEditMode = this.viewModeService.isEditMode();
  }

  /**
   * Angular init hook
   */
  ngOnInit(): void {
    this.setBreadcrumbItems();
  }

  /**
   * Get content from document
   */
  get content() {
    return this.document?.getData<LitBannerContent>();
  }

  /**
   * Load icons for cards
   */
  get cards() {
    if (this.content.cards) {
      for (const card of this.content.cards) {
        if (card.link?.linkCollection?.length) {
          card.link = card.link.linkCollection[0];
          card.text = card.link?.displayText;
          card.externalLink = card.link?.external;
          card.isNewWindow = card.link?.target === '_blank' ? true : false;
          card.href = this.ctaLinkService.getCTALink(
            this.page,
            card.link,
            this.appStateService?.getHomePageUrl()
          );
          card.signInRequired = card.link?.signInLink;
        }
        if (card.iconPath && card.iconPath.widenAsset) {
          card.img = this.widenService.getWidenAssetUrl(
            card.iconPath.widenAsset
          );
          card.icon = card.img;
        }
      }
    }
    return this.content.cards;
  }

  /**
   * Get banner type (theme)
   */
  get type() {
    return this.component.getParameters().type;
  }
}
